import { AxiosInstance, isAxiosError } from "axios"
import {
  DummyDataState,
  DummyGenerateRequest,
  DummyGenerateResult,
} from "types/dummyData.types"
import { errMsgOf } from "utils/errMsgOf"

/**
 * Dummy data API functions (Conductor routes). Authentication headers are
 * handled by the axios instance.
 */

/**
 * Reads the project's dummy data state, which the server reads from the DQD's
 * provenance (its own metadata): whether the data is dummy data, and the
 * run's profile, time and map naming.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param options - `csv`: also ask whether `$data.csv` is still the run's
 *   (`csvIsDummy`). The server then reads and hashes the whole CSV, so only
 *   an action that needs the answer asks for it.
 * @returns `active` and the provenance, or `{}` when the data is not dummy data.
 */
export async function getDummyData(
  axiosInstance: AxiosInstance,
  id: string,
  options?: { csv?: boolean }
): Promise<DummyDataState> {
  const res = await axiosInstance.get(
    `/dummy-data-get?id=${encodeURIComponent(id)}${options?.csv ? "&csv" : ""}`
  )
  return (
    res.data && typeof res.data === "object" ? res.data : {}
  ) as DummyDataState
}

/**
 * The words of the one 409 that asks to replace the project's imported data.
 * The Conductor answers 409 for other conflicts too (a run already in
 * progress, real data arriving during the run): those are plain failures.
 */
const REPLACE_CONFLICT = "already has imported data"

/**
 * Generates dummy data from the questionnaire's routing and imports it.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID.
 * @param request - The profile, the map options (used only when the project has no map) and `replace`.
 * @returns The generator's report, the import warnings and any warning about what followed the import.
 * @throws The axios error itself only for the 409 that asks to replace imported data (a request
 *   that did not set `replace`, whose `Err msg` says it "already has imported data") and for a
 *   failure with no `{"Err msg": ...}` body other than a 409 (a network error, for example);
 *   otherwise an Error carrying the server's `Err msg` (or the status text of a 409 without one),
 *   whether the status was an error status or 200.
 */
export async function generateDummyData(
  axiosInstance: AxiosInstance,
  id: string,
  request: DummyGenerateRequest
): Promise<DummyGenerateResult> {
  let res
  try {
    res = await axiosInstance.post(
      `/dummy-data-generate?id=${encodeURIComponent(id)}`,
      request
    )
  } catch (e) {
    // Most Conductor failures are non-2xx with an `Err msg` body; surface that
    // message instead of "Request failed with status code 500". Only the 409
    // that says the project has imported data stays an axios error, so callers
    // recognise it and offer to replace; a 409 to a request that already
    // replaces is never that one, and a 409 that does not say so (no body, or
    // another conflict) must never lead to a replacing resend.
    if (isAxiosError(e) && e.response) {
      const msg = errMsgOf(e.response.data)
      if (e.response.status === 409) {
        const asksToReplace =
          request.replace !== true &&
          msg !== null &&
          msg.includes(REPLACE_CONFLICT)
        if (!asksToReplace) throw new Error(msg ?? e.message)
      } else if (msg !== null) throw new Error(msg)
    }
    throw e
  }
  const data = res.data as Record<string, unknown>
  if (data && typeof data === "object" && "Err msg" in data)
    throw new Error(String(data["Err msg"]))
  return data as unknown as DummyGenerateResult
}
